import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MobileLayout from '../../components/MobileLayout';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

const formats = [
  { value: 'novel', label: '📖 Novel', desc: 'Full-length book' },
  { value: 'short_story', label: '📝 Short Story', desc: 'Compact narrative' },
  { value: 'fan_fiction', label: '✨ Fan Fiction', desc: 'Inspired by existing works' },
  { value: 'screenplay', label: '🎬 Screenplay', desc: 'Script format' },
  { value: 'poetry', label: '🎭 Poetry', desc: 'Verse collection' },
];

const genreOptions = [
  'Romance',
  'Fantasy',
  'Sci-Fi',
  'Thriller',
  'Horror',
  'Literary Fiction',
  'Mystery',
  'Adventure',
  'Drama',
  'Comedy',
  'Historical',
  'Dystopian',
];

const toneOptions = ['Dark', 'Light', 'Humorous', 'Dramatic', 'Poetic', 'Gritty', 'Whimsical'];
const povOptions = [
  'First Person',
  'Third Person Limited',
  'Third Person Omniscient',
  'Second Person',
];
const lengthOptions = [
  { value: 'short', label: 'Short (~5K words)' },
  { value: 'medium', label: 'Medium (~20K words)' },
  { value: 'novel', label: 'Novel (~60K+ words)' },
  { value: 'epic', label: 'Epic (~100K+ words)' },
];

const CreateStory: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [format, setFormat] = useState('novel');
  const [genres, setGenres] = useState<string[]>([]);
  const [tone, setTone] = useState('');
  const [pov, setPov] = useState('');
  const [targetLength, setTargetLength] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const toggleGenre = (genre: string) => {
    setGenres((prev) =>
      prev.includes(genre) ? prev.filter((g) => g !== genre) : [...prev, genre],
    );
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error(t('stories.titleRequired', 'Please enter a title'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post('/stories', {
        title: title.trim(),
        format,
        genres,
        tone: tone || undefined,
        pov: pov || undefined,
        targetLength: targetLength || undefined,
      });
      navigate(`/stories/${res.data.id}`);
    } catch (error) {
      toast.error(t('stories.createError', 'Failed to create story'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MobileLayout headerTitle={t('stories.createStory', 'New Story')} showBackButton>
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Title */}
        <div>
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: 'var(--ds-text-primary)' }}
          >
            {t('stories.titleLabel', 'Title')}
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('stories.titlePlaceholder', 'Your story title...')}
            className="w-full px-4 py-3 rounded-xl border bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500"
            style={{
              borderColor: 'var(--ds-border)',
              color: 'var(--ds-text-primary)',
            }}
          />
        </div>

        {/* Format */}
        <div>
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: 'var(--ds-text-primary)' }}
          >
            {t('stories.formatLabel', 'Format')}
          </label>
          <div className="grid grid-cols-2 gap-2">
            {formats.map((f) => (
              <button
                key={f.value}
                onClick={() => setFormat(f.value)}
                className={`p-3 rounded-xl border text-left transition-all ${
                  format === f.value ? 'ring-2 ring-primary-500' : ''
                }`}
                style={{
                  borderColor:
                    format === f.value ? 'var(--ds-accent, #8b5cf6)' : 'var(--ds-border)',
                  background: format === f.value ? 'rgba(139,92,246,0.1)' : 'var(--ds-bg-card)',
                }}
              >
                <div className="font-medium text-sm" style={{ color: 'var(--ds-text-primary)' }}>
                  {f.label}
                </div>
                <div className="text-xs mt-0.5" style={{ color: 'var(--ds-text-secondary)' }}>
                  {f.desc}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Genres */}
        <div>
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: 'var(--ds-text-primary)' }}
          >
            {t('stories.genreLabel', 'Genres')}
          </label>
          <div className="flex flex-wrap gap-2">
            {genreOptions.map((genre) => (
              <button
                key={genre}
                onClick={() => toggleGenre(genre)}
                className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                  genres.includes(genre) ? 'bg-primary-500 text-white' : ''
                }`}
                style={
                  !genres.includes(genre)
                    ? {
                        background: 'var(--ds-bg-card)',
                        border: '1px solid var(--ds-border)',
                        color: 'var(--ds-text-secondary)',
                      }
                    : {}
                }
              >
                {genre}
              </button>
            ))}
          </div>
        </div>

        {/* Tone */}
        <div>
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: 'var(--ds-text-primary)' }}
          >
            {t('stories.toneLabel', 'Tone')}
          </label>
          <select
            value={tone}
            onChange={(e) => setTone(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500"
            style={{
              borderColor: 'var(--ds-border)',
              color: 'var(--ds-text-primary)',
            }}
          >
            <option value="">{t('stories.selectTone', 'Select tone...')}</option>
            {toneOptions.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>

        {/* POV */}
        <div>
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: 'var(--ds-text-primary)' }}
          >
            {t('stories.povLabel', 'Point of View')}
          </label>
          <select
            value={pov}
            onChange={(e) => setPov(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500"
            style={{
              borderColor: 'var(--ds-border)',
              color: 'var(--ds-text-primary)',
            }}
          >
            <option value="">{t('stories.selectPov', 'Select POV...')}</option>
            {povOptions.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>

        {/* Target Length */}
        <div>
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: 'var(--ds-text-primary)' }}
          >
            {t('stories.lengthLabel', 'Target Length')}
          </label>
          <select
            value={targetLength}
            onChange={(e) => setTargetLength(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500"
            style={{
              borderColor: 'var(--ds-border)',
              color: 'var(--ds-text-primary)',
            }}
          >
            <option value="">{t('stories.selectLength', 'Select length...')}</option>
            {lengthOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={submitting || !title.trim()}
          className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white rounded-xl font-semibold transition-colors"
        >
          {submitting
            ? t('stories.creating', 'Creating...')
            : t('stories.createButton', 'Create Story')}
        </button>
      </div>
    </MobileLayout>
  );
};

export default CreateStory;
